import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PasswordInput } from "@/components/ui/PasswordInput";
import { platformClient, formatPlatformError, isPasswordStrong, PASSWORD_POLICY_MSG } from "./platformApi";

export default function PlatformResetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [password, setPassword] = useState("");
    const [repeat, setRepeat] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (password !== repeat) {
            setError("Wachtwoorden komen niet overeen");
            return;
        }
        if (!isPasswordStrong(password)) {
            setError(PASSWORD_POLICY_MSG);
            return;
        }
        setBusy(true);
        setError("");
        try {
            await platformClient.post("/auth/reset-password", { token, new_password: password });
            setDone(true);
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="reset-password-page" className="flex min-h-screen items-center justify-center bg-ink px-6">
            <div className="w-full max-w-sm border border-white/10 bg-coal p-8">
                <div className="flex flex-col items-center">
                    <img
                        src="/logo/cbs-lockup-dark.svg"
                        alt="Clarke Business Solutions"
                        data-testid="reset-logo-lockup"
                        className="w-40"
                    />
                    <p className="mt-4 text-[11px] uppercase tracking-[0.3em] text-ash">
                        Nieuw wachtwoord instellen
                    </p>
                </div>
                {!token ? (
                    <div className="mt-8 space-y-6">
                        <p data-testid="reset-no-token" className="text-sm leading-relaxed text-red-400">
                            Deze link is ongeldig. Vraag een nieuwe herstel-link aan.
                        </p>
                        <Link
                            data-testid="reset-to-login-link"
                            to="/platform"
                            className="block w-full border border-white/15 px-8 py-4 text-center text-xs uppercase tracking-[0.2em] text-ash transition-colors hover:border-gold/60 hover:text-gold"
                        >
                            Naar inloggen
                        </Link>
                    </div>
                ) : done ? (
                    <div className="mt-8 space-y-6">
                        <p data-testid="reset-success-message" className="text-sm leading-relaxed text-ash">
                            Je wachtwoord is bijgewerkt. Je kunt nu inloggen met je nieuwe wachtwoord.
                        </p>
                        <Link
                            data-testid="reset-success-login-link"
                            to="/platform"
                            className="block w-full bg-gold px-8 py-4 text-center text-xs uppercase tracking-[0.2em] text-black transition-colors hover:bg-bone"
                        >
                            Inloggen
                        </Link>
                    </div>
                ) : (
                    <form onSubmit={submit} className="mt-8 space-y-6">
                        <label className="block">
                            <span className="text-[11px] uppercase tracking-[0.3em] text-ash">Nieuw wachtwoord</span>
                            <PasswordInput
                                testid="reset-password-input"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                minLength={10}
                                className="mt-3 w-full border-b border-white/20 bg-transparent pb-3 text-base text-bone transition-colors focus:border-gold focus:outline-none"
                            />
                        </label>
                        <label className="block">
                            <span className="text-[11px] uppercase tracking-[0.3em] text-ash">Herhaal wachtwoord</span>
                            <PasswordInput
                                testid="reset-password-repeat-input"
                                value={repeat}
                                onChange={(e) => setRepeat(e.target.value)}
                                className="mt-3 w-full border-b border-white/20 bg-transparent pb-3 text-base text-bone transition-colors focus:border-gold focus:outline-none"
                            />
                        </label>
                        <p className="text-[11px] text-ash/70">{PASSWORD_POLICY_MSG}</p>
                        {error ? (
                            <p data-testid="reset-error" className="text-sm text-red-400">
                                {error}
                            </p>
                        ) : null}
                        <button
                            data-testid="reset-submit-button"
                            type="submit"
                            disabled={busy}
                            className="w-full bg-gold px-8 py-4 text-xs uppercase tracking-[0.2em] text-black transition-colors hover:bg-bone disabled:opacity-60"
                        >
                            {busy ? "Opslaan…" : "Wachtwoord opslaan"}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
